/**
 * Invites — founder / workspace-admin only.
 *
 * `POST …/invites` answers with the link exactly once, and this panel therefore
 * shows it once, in a selectable field. It says the link will not be retrievable
 * afterwards, because it will not be — the server stores only its hash. A
 * revoked invite stays in the list with its state, so "wir haben den Link
 * zurückgezogen" is something the reader can see, not something they have to
 * remember.
 *
 * Whether the invitee ALSO got a mail is `mail_sent` on that response, and this
 * panel reads it rather than assuming. It is now the server's report of what
 * actually happened: false with the console default, true over a configured
 * SMTP transport — and false again when a configured transport failed, which is
 * exactly the case where the founder has to notice and relay the link by hand.
 */
import { useState } from 'react'
import { Copy, Send, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n, type TranslationKey } from '@/i18n'
import { roleLabel } from '@/i18n/enumLabels'
import { useAsync } from '@/lib/useAsync'
import type { InviteCreated, InviteRef } from '@/api/types'

const STATE_LABEL: Record<string, TranslationKey> = {
  open: 'invite.state.open',
  accepted: 'invite.state.accepted',
  revoked: 'invite.state.revoked',
  expired: 'invite.state.expired',
}

export function InvitePanel({ workspaceId }: { workspaceId: string }) {
  const { t } = useI18n()
  const invites = useAsync<InviteRef[]>((signal) => api.invites(workspaceId, signal), [workspaceId])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'member' | 'admin'>('member')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The whole answer, not just the URL: `mail_sent` decides what the founder is
  // told to do next, and it is only knowable from this response.
  const [created, setCreated] = useState<InviteCreated | null>(null)

  const create = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setCreated(null)
    try {
      const created = await api.createInvite(workspaceId, email.trim(), role)
      setCreated(created)
      setEmail('')
      invites.reload()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (inviteId: string) => {
    setError(null)
    try {
      await api.revokeInvite(workspaceId, inviteId)
      invites.reload()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invitePanel.title')}</CardTitle>
        <CardDescription>{t('invitePanel.lead')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void create(e)}>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-email" className="text-[13px] font-medium text-ink-2">
              {t('invitePanel.emailLabel')}
            </label>
            <input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="h-10 w-[260px] max-w-full rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-role" className="text-[13px] font-medium text-ink-2">
              {t('invitePanel.roleLabel')}
            </label>
            <select
              id="invite-role"
              value={role}
              onChange={(event) => setRole(event.target.value === 'admin' ? 'admin' : 'member')}
              className="h-10 rounded-sm border border-border-strong bg-white px-2 text-[15px] text-ink"
            >
              <option value="member">{t('invitePanel.roleMember')}</option>
              <option value="admin">{t('invitePanel.roleAdmin')}</option>
            </select>
          </div>
          <Button type="submit" disabled={busy || email.trim() === ''}>
            <Icon icon={Send} />
            {t('invitePanel.create')}
          </Button>
        </form>

        {created ? (
          <div
            role="status"
            data-testid="invite-link"
            data-mail-sent={String(created.mail_sent)}
            className="flex flex-col gap-2 rounded-sm border border-border bg-accent-soft px-4 py-3"
          >
            <p className="text-[13px] font-medium text-ink-2">{t('invitePanel.linkOnce')}</p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                readOnly
                value={created.invite_url}
                aria-label={t('invitePanel.linkOnce')}
                className="num h-9 min-w-0 grow rounded-sm border border-border bg-white px-2 text-[12px] text-ink"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void navigator.clipboard?.writeText(created.invite_url)}
              >
                <Icon icon={Copy} />
                {t('invitePanel.copy')}
              </Button>
            </div>
            <p className="text-[12px] text-muted">
              {t(created.mail_sent ? 'invitePanel.mailSent' : 'invitePanel.noMail')}
            </p>
          </div>
        ) : null}

        {error ? <ErrorNote message={error} /> : null}

        {invites.error ? (
          <ErrorNote message={invites.error} onRetry={invites.reload} retryLabel={t('common.retry')} />
        ) : null}

        <ul className="flex flex-col" data-testid="invite-list">
          {(invites.data ?? []).map((invite) => (
            <li
              key={invite.id}
              data-invite-state={invite.state}
              className="flex flex-wrap items-center gap-3 border-b border-border py-2 last:border-b-0"
            >
              <span className="text-[13px] text-ink">{invite.email}</span>
              <Badge variant="neutral">{t(STATE_LABEL[invite.state] ?? 'invite.state.open')}</Badge>
              <span className="text-[12px] text-muted">{roleLabel(invite.role, t)}</span>
              {invite.state === 'open' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ms-auto"
                  onClick={() => void revoke(invite.id)}
                >
                  <Icon icon={Trash2} />
                  {t('invitePanel.revoke')}
                </Button>
              ) : null}
            </li>
          ))}
          {invites.data?.length === 0 ? (
            <li className="py-2 text-[13px] text-muted">{t('invitePanel.empty')}</li>
          ) : null}
        </ul>
      </CardContent>
    </Card>
  )
}
